import { AioExternalSingle } from "../additional";
import { GridItemTrafficLight } from "../table";
import { MosaicTrafficLightsValue } from "../traffic-lights";
import { defaultEditorDefns, EditorDefns } from "../utility";
import { AibBlockLine, AibLineType } from "./interface";

export interface IBlockState<T> {
  id: string;
  canAddFirstRow: boolean;
  canChangeType: boolean;
  changed: boolean;
  changedBlocks: GridItemTrafficLight[];
  defaultType: AibLineType;
  disabled: boolean;
  editorProps: EditorDefns<T>;
  externalSingles: AioExternalSingle<T>[];
  lineStyle: React.CSSProperties;
  lines: AibBlockLine<T>[];
  maxLines: number;
  minLines: number;
  noComments: boolean;
  noTrafficLights: boolean;
}

export const defaultBlockState: IBlockState<unknown> = {
  id: "default-block-id",
  canAddFirstRow: false,
  canChangeType: false,
  changed: false,
  changedBlocks: [],
  disabled: true,
  editorProps: defaultEditorDefns as EditorDefns<unknown>,
  externalSingles: [],
  defaultType: AibLineType.leftOnly,
  lines: [],
  lineStyle: {},
  minLines: 1,
  maxLines: 10,
  noComments: false,
  noTrafficLights: false,
};

export const ADD_LINE = "ADD_LINE";
export const CHANGE_PUSHED = "CHANGE_PUSHED";
export const REMOVE_LINE = "REMOVE_LINE";
export const SET_STATE = "SET_STATE";
export const UPDATE_BLOCK_CELL = "UPDATE_BLOCK_CELL";
export const UPDATE_LINE = "UPDATE_LINE";

type BlockOperation =
  | typeof ADD_LINE
  | typeof CHANGE_PUSHED
  | typeof REMOVE_LINE
  | typeof SET_STATE
  | typeof UPDATE_BLOCK_CELL
  | typeof UPDATE_LINE;

interface IBlockActionBase {
  operation: BlockOperation;
}

interface IBlockActionAddLine<T> extends IBlockActionBase {
  operation: typeof ADD_LINE;
  line: AibBlockLine<T>;
  ix: number;
}

interface IBlockActionChangePushed extends IBlockActionBase {
  operation: typeof CHANGE_PUSHED;
}

interface IBlockActionRemoveLine extends IBlockActionBase {
  operation: typeof REMOVE_LINE;
  aifid: string;
}

interface IBlockActionSetState<T> extends IBlockActionBase {
  operation: typeof SET_STATE;
  id: string;
  canChangeType: boolean;
  changedBlocks: GridItemTrafficLight[];
  defaultType: AibLineType;
  disabled: boolean;
  editorProps: EditorDefns<T>;
  externalSingles: AioExternalSingle<T>[];
  lineStyle: React.CSSProperties;
  lines: AibBlockLine<T>[];
  maxLines: number;
  minLines: number;
  noTrafficLights: boolean;
}

interface IBlockActionUpdateCell<T> extends IBlockActionBase {
  operation: typeof UPDATE_BLOCK_CELL;
  aifid: string;
  position: "left" | "center" | "right";
  cellContent: T;
}

interface IBlockActionUpdateLine<T> extends IBlockActionBase {
  operation: typeof UPDATE_LINE;
  line: AibBlockLine<T>;
}

export type IBlockAction<T> =
  | IBlockActionAddLine<T>
  | IBlockActionChangePushed
  | IBlockActionRemoveLine
  | IBlockActionSetState<T>
  | IBlockActionUpdateCell<T>
  | IBlockActionUpdateLine<T>;

/**
 * Reducer function for managing the state of a block.
 *
 * @param state - The current block reducer state
 * @param action - The action to apply to the block state
 * @returns IBlockState<T>
 */
export const blockReducer = <T>(state: IBlockState<T>, action: IBlockAction<T>): IBlockState<T> => {
  // Do nothing if disabled
  if (state.disabled && action.operation !== SET_STATE) {
    return state;
  }
  // Create new state
  let newState = { ...state };
  const errCheck: string[] = [];

  // Check action type
  switch (action.operation) {
    case ADD_LINE: {
      if (action.ix < 0 || action.ix > newState.lines.length) {
        errCheck.push("ADD_LINE: index out of bounds");
      }
      if (newState.lines.length >= state.maxLines) {
        errCheck.push("ADD_LINE: max lines reached");
      }
      if (errCheck.length > 0) {
        break;
      }
      /* istanbul ignore else */
      if (!newState.lines.some((line) => line.aifid === action.line.aifid)) {
        const lineWithTrafficLight = {
          ...action.line,
          coreTrafficLight: "ChangedUnknown" as MosaicTrafficLightsValue,
        };
        newState.lines = [
          ...newState.lines.slice(0, action.ix),
          lineWithTrafficLight,
          ...newState.lines.slice(action.ix),
        ];
      }
      newState.changed = true;
      break;
    }
    case CHANGE_PUSHED: {
      newState.changed = false;
      break;
    }
    case REMOVE_LINE: {
      const ix = newState.lines.findIndex((line) => line.aifid === action.aifid);
      if (ix === -1) {
        errCheck.push("REMOVE_LINE: line not found");
      }
      if (newState.lines.length <= state.minLines) {
        errCheck.push("REMOVE_LINE: min lines reached");
      }
      if (errCheck.length > 0) {
        break;
      }
      newState.lines = [...newState.lines.slice(0, ix), ...newState.lines.slice(ix + 1)];
      newState.changed = true;
      break;
    }
    case SET_STATE: {
      newState = {
        ...newState,
        id: action.id,
        canChangeType: action.canChangeType,
        changed: false,
        changedBlocks: action.changedBlocks,
        defaultType: action.defaultType,
        disabled: action.disabled,
        editorProps: action.editorProps,
        externalSingles: action.externalSingles,
        lineStyle: action.lineStyle,
        lines: action.lines,
        maxLines: action.maxLines,
        minLines: action.minLines,
        noTrafficLights: action.noTrafficLights,
      };
      break;
    }
    case UPDATE_BLOCK_CELL: {
      const ix = newState.lines.findIndex((line) => line.aifid === action.aifid);
      if (ix === -1) {
        errCheck.push("UPDATE_BLOCK_CELL: line not found");
        break;
      }
      const updatedLine = {
        ...newState.lines[ix],
        [action.position]: action.cellContent,
        coreTrafficLight: "ChangedUnknown" as MosaicTrafficLightsValue,
      };
      newState.lines = [
        ...newState.lines.slice(0, ix),
        updatedLine,
        ...newState.lines.slice(ix + 1),
      ];
      newState.changed = true;
      break;
    }
    case UPDATE_LINE: {
      const ix = newState.lines.findIndex((line) => line.aifid === action.line.aifid);
      if (ix === -1) {
        errCheck.push("UPDATE_LINE: line not found");
        break;
      }
      newState.lines[ix] = action.line;
      newState.changed = true;
      break;
    }
  }

  if (errCheck.length > 0) {
    console.warn(errCheck);
    return state;
  }
  return newState;
};
